/** @file eulerYawPitchOf: quatFromYawPitchRoll inverted to yaw and pitch (degrees), roll dropped. */
import type { Quat } from './quat';
import { quatRotateVec3 } from './quatRotateVec3';

const DEG = 180 / Math.PI;
/** Horizontal length of the forward vector below which it counts as vertical (gimbal). */
const VERTICAL = 1e-12;

/**
 * `[yawDeg, pitchDeg]`, yaw in [0, 360), pitch in [-180, 180). Forward f = q (0, -1, 0) gives yaw from its
 * heading and pitch in (-90, 90) from its elevation. Every rotation has a second reading (yaw + 180,
 * 180 - pitch, roll + 180); the one whose roll lies within +-90 is returned, so a node tipped past vertical
 * reports |pitch| > 90 with its own yaw. When forward is vertical the yaw is undefined: `[0, +-90]`. This is the Euler
 * inverse editors need to turn a rotation back into key numbers; the facet read-back is `yawPitchOf` (21.1), which
 * differs for rolled, steep rotations.
 */
export function eulerYawPitchOf(q: Quat): [number, number] {
  const [fx, fy, fz] = quatRotateVec3(q, [0, -1, 0]);
  const h = Math.hypot(fx, fy);
  if (h < VERTICAL) return [0, fz > 0 ? 90 : -90];
  // Heading of yaw y is (-sin y, -cos y): clockwise from south seen from above.
  let yaw = Math.atan2(-fx, -fy) * DEG;
  let pitch = Math.atan2(fz, h) * DEG;
  // cos(roll) = up . (up of the roll-free reading) = up . (-sin(pitch) heading + cos(pitch) z),
  // with heading = (fx, fy, 0) / h, sin(pitch) = fz and cos(pitch) = h.
  const [ux, uy, uz] = quatRotateVec3(q, [0, 0, 1]);
  const cosRoll = (-fz * (ux * fx + uy * fy)) / h + h * uz;
  if (cosRoll < 0) {
    yaw += 180;
    pitch = pitch >= 0 ? 180 - pitch : -180 - pitch;
  }
  yaw = ((yaw % 360) + 360) % 360;
  return [yaw >= 360 ? 0 : yaw, pitch >= 180 ? pitch - 360 : pitch];
}
